import React, { Fragment, useState, useEffect } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { FundingStagesCell, LoanTypeIcon } from '../../components';
import { formatDate, formatTime, centsToDollar, aprToPercent, timeAgo, formatCodes, stage2Text } from '../../utils/formatters';
import { LoanService } from '../../services';
import { getLoanStatusColor, commentCategories } from '../../utils/constants';
import { Grid, Typography, TextField, Link, Breadcrumbs,  Tooltip, List, Button,
         IconButton, ListItem, Divider, ListItemText, ListItemAvatar, Avatar, Chip } from '@material-ui/core';
import LoanNotificationsModal from './LoanNotificationsModal';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector,
         TimelineContent, TimelineDot, TimelineOppositeContent } from '@material-ui/lab';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import PanToolIcon from '@material-ui/icons/PanTool';
import KeyboardReturnIcon from '@material-ui/icons/KeyboardReturn';
import MoneyOffIcon from '@material-ui/icons/MoneyOff';
import DialpadIcon  from '@material-ui/icons/Dialpad';
import DelinquencyIcon from '@material-ui/icons/History';
import ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import PhoneIcon from '@material-ui/icons/Phone';
import NoteIcon from '@material-ui/icons/Note';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import WarningIcon from '@material-ui/icons/Warning';
import MailIcon from '@material-ui/icons/Mail';
import DoneIcon from '@material-ui/icons/Done';
import CallMissedOutgoingIcon from "@material-ui/icons/CallMissedOutgoing";
import { AssignmentTurnedIn, Dvr } from '@material-ui/icons';
import { useParams } from "react-router-dom";
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import { blue, grey, blueGrey, red, yellow, green } from '@material-ui/core/colors';
import ConfirmationModal from '../../components/ConfirmationModal';
import BounceMarkerModal from '../../components/BounceMarkerModal';
import AlertModal from '../../components/AlertModal';
import FRPRModal from '../../components/FRPRModal'
import ReturnsProcessedMarkerModal from '../../components/ReturnsProcessedMarkerModal';
import { useNotification } from '../../contexts/NotificationContext';
import { useError } from '../../contexts/ErrorContext';
import { useRole } from '../../contexts/RolesContext';
import { isSuccessfulResponse } from '../../utils/responseHelpers';
import { createBrowserHistory } from "history";
import HistoryDiff from '../../components/HistoryDiff';

const styles = makeStyles((theme) => ({
    root: {
    },
    summary: {
        backgroundColor: theme.palette.grey.main,
        borderRadius: '5px',
        border: 'groove',
        borderColor: 'black',
        borderWidth: '0.5px',
        margin: "25px",
        padding: "10px",
        width: "25vw",
    },
    summaryHeader: {
        textAlign: 'center',
        textDecoration: 'underline',
    },
    summaryDownload: {
        float: 'right'
    },
    summaryUL: {
        listStylePosition: 'inside',
    },
    secondRow: {
        borderRadius: '5px',
        border: 'groove',
        borderColor: 'black',
        borderWidth: '0.5px',
        margin: "25px",
        padding: "10px",
        width: "40vw",
    },
    calculatorBtn: {
        display: 'block',
        marginLeft: 'auto',
        marginRight: '1px'
    },
    calculatorUL: {
        listStylePosition: 'inside',
        listStyle: 'none'
    },
    historyTimelineWrapper: {
        left: '-8vw',
        position: 'relative',
        maxHeight: '60vh',
        overflowY: 'scroll'
    },
    notesWrapper: {
        maxHeight: '60vh',
        overflowY: 'scroll'
    },
    pageHeader: {
        display: 'flex',
        justifyContent: 'center',
    },
    pageHeaderChip: {
        marginTop: '20px',
        marginLeft: '40px',
        fontWeight: 'bold',
        fontSize: '18px',
    },
    comment: {
        margin: '10px 10px 10px 5px',
        width: '40%'
    },
    commentType: {
        margin: '10px 5px 10px 20px',
    },
    commentButton: {
        margin: '15px 10px 10px 10px',
    },
    commentDate: {
        fontSize: '12px',
    },
    commentDivider: {
        paddingLeft: '5px',
        paddingRight: '10px'
    },
    selectInput: {
        margin: '5px 5px 5px 10px',
        width: '50%',
    },
    milestones: {
        display: 'flex',
    },
    alertContainer: {
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeeba',
        borderRadius: '6px',
        padding: '2px',
        paddingLeft: '5px'
    },
    alertTitle: {
        padding: '3px',
        paddingLeft: '10px',
        margin: '0px'
    }
}));

const gatherHistory = (loanHistoryData) => {
    let items = [];

    items.push(FundingRequestHistory2HTML(loanHistoryData.fundingRequest));
    items.push(ReturnHistory2HTML(loanHistoryData.refunds));
    items.push(PaymentHistory2HTML(loanHistoryData.payments));

    return items
        .flat()
        .filter(Boolean)
        .sort((a, b) => (a.date > b.date ? 1 : -1))
        .map((r) => r.cmp)
}

const ReturnHistory2HTML = (refunds) => {
    if (!refunds || refunds.length === 0) return null;

    let items = [];

    refunds.forEach(refund => {
        if (refund.processedAt) {
            items.push({
                date: refund.processedAt,
                message: `Return processed (${centsToDollar( refund.amountCents )})`,
                icon: (
                    <TimelineDot style={{ backgroundColor: green[700] }}>
                        <DoneIcon />
                    </TimelineDot>
                ),
                bounced: refund.bouncedAt ? true : false,
            });
        }

        if (refund.bouncedAt) {
            items.push({
                date: refund.bouncedAt,
                message: `Return bounced (${centsToDollar(refund.amountCents)})`,
                icon: (
                    <TimelineDot style={{ backgroundColor: red[500] }}>
                        <CallMissedOutgoingIcon />
                    </TimelineDot>
                ),
                bounced: true
            })
        }

        if (refund.issuedAt) {
            items.push({
                date: refund.issuedAt,
                message: `Return Issued (${centsToDollar(refund.amountCents)})`,
                icon: (
                    <TimelineDot style={{ backgroundColor: grey[500] }}>
                        <KeyboardReturnIcon />
                    </TimelineDot>
                ),
                bounced: refund.bouncedAt ? true : false
            })
        }
    });

    return items.map(item => GenerateHistoryItem(item, 4, item.bounced));
}

const FundingRequestHistory2HTML = (fundingRequest) => {
    if (!fundingRequest) return null;

    let message = '';
    let icon = null;

    if (fundingRequest.status === 'held') {
        message = `Funding Request held (${centsToDollar(fundingRequest.amountCents)})`;
        icon = (<TimelineDot style={{backgroundColor: red[500]}}>
                <PanToolIcon />
            </TimelineDot>);
    } else {
        message = `Funding Request ${fundingRequest.status} (${centsToDollar(fundingRequest.amountCents)})`;
        let color = fundingRequest.status === 'funded' ? green[700] : grey[500];

        icon = <TimelineDot style={{ backgroundColor: color }}><AccountBalanceIcon /></TimelineDot>;
    }

    const historyItem = {
        ...fundingRequest,
        icon,
        message,
        completed: false,
        date: fundingRequest[`${fundingRequest.status}At`],
    };
    return GenerateHistoryItem(historyItem, 2);
}

const PaymentHistory2HTML = (payments) => {
    if (!payments || payments.length === 0) return null;

    let items = [];

    payments.forEach(payment => {
        const msgPrefix = payment.bounced ? `[BOUNCED] ` : '';
        const stage = stage2Text(payment.stage);
        const amount = centsToDollar(payment.amountCents);

        const paidAt = formatDate(payment.paidAt);

        if (payment.receivedAt) {
            items.push({
                message: `${msgPrefix}${stage} payment received (${amount})`,
                date: payment.receivedAt,
                icon: (
                    <TimelineDot style={{ backgroundColor: blueGrey[600] }}>
                        <MailIcon />
                    </TimelineDot>
                )
            });
        }

        if (payment.bouncedAt) {
            items.push({
                message: `${stage} payment bounced (${amount})`,
                date: payment.bouncedAt,
                icon: (
                    <TimelineDot style={{ backgroundColor: red[500] }}>
                        <CallMissedOutgoingIcon />
                    </TimelineDot>
                )
            });
        }

        if (payment.paidAt) {
            items.push({
                message: `${msgPrefix}${stage} payment paid (${amount})`,
                date: payment.paidAt,
                icon: (
                    <TimelineDot style={{ backgroundColor: green[700] }}>
                        <DoneIcon />
                    </TimelineDot>
                ),
                bounced: payment.bounced,
            });
        }

        if (payment.heldAt && formatDate(payment.heldAt) !== paidAt) {
            items.push({
                message: `${msgPrefix}${stage} payment held (${amount})`,
                date: payment.heldAt,
                icon: (
                    <TimelineDot style={{ backgroundColor: red[600] }}>
                        <PanToolIcon fontSize="small" />
                    </TimelineDot>
                ),
                bounced: payment.bounced,
            });
        }

        if (payment.processingAt && formatDate(payment.processingAt) !== paidAt) {
            items.push({
                message: `${msgPrefix}${stage} payment processing (${amount})`,
                date: payment.processingAt,
                icon: (
                    <TimelineDot style={{ backgroundColor: yellow[600] }}>
                        <WarningIcon />
                    </TimelineDot>
                ),
                bounced: payment.bounced,
            });
        }

        if (!payment.receivedAt && !payment.paidAt && !payment.processingAt && !payment.heldAt) {
            items.push({
                message: `${msgPrefix}${stage} payment completed (${amount})`,
                date: payment.receivedAt,
                completed:
                    payment.stage === "unstaged" || payment.stage === "final",
                icon: (
                    <TimelineDot color="secondary">
                        <AttachMoneyIcon />
                    </TimelineDot>
                ),
                bounced: payment.bounced,
            });
        }
    });

    return items.map(item => GenerateHistoryItem(item, 3, item.bounced));
}

const GenerateHistoryItem = (item, tab, bounced) => {
    const { isAdmin, isDealerBrowser } = useRole();

    return {
        date: item.date,
        cmp: (
            <TimelineItem>
                <TimelineOppositeContent>
                    <Typography variant="body2" color="textSecondary">
                    {isAdmin() || isDealerBrowser() ? (
                        <a
                        href={`/lms/daily-processing/?date=${encodeURIComponent(
                            formatDate(item.date)
                        )}&tab=${tab}`}
                        style={{ color: grey[600] }}
                        target="_blank"
                        rel="noopener noreferrer"
                        >
                        {formatDate(item.date)}
                        </a>
                    ) : (
                        formatDate(item.date)
                    )}
                    </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                    {item.icon}
                    {!item.completed ? <TimelineConnector /> : null}
                </TimelineSeparator>
                <TimelineContent style={{ backgroundColor: 'transparent', borderRadius: "20px" }}>
                    <Typography component="span" style={ bounced ? { color: red[500] } : {} } >{item.message}</Typography>
                    <Typography component="span" variant="body2">
                        { item.holdComment && item.isHeld ? `  Reason: '${item.holdComment}'` : null }
                    </Typography>
                </TimelineContent>
            </TimelineItem>
        )
    };
}

const Delinquency2HTML = (delinquencyItem) => {
    let paidDate = delinquencyItem.isDelinquent ? null : new Date(delinquencyItem.dueDate);
    let princBalance = 'Remaining Balance: ' + centsToDollar(delinquencyItem.currentPrincipalBalance);
    let message = 'Delinquent: '+ delinquencyItem.daysPastDue+' days';
    let icon = <TimelineDot style={{backgroundColor: red[500]}} ><DelinquencyIcon /></TimelineDot>;;
    let payment = null;

    if(paidDate) {
        paidDate.setDate(paidDate.getDate() + delinquencyItem.daysPastDue)
        payment = 'Paid:'+ formatDate(paidDate);
        icon = <TimelineDot style={{backgroundColor: grey[400]}} ><DelinquencyIcon /></TimelineDot>;;
    }

    return (
        <TimelineItem>
            <TimelineOppositeContent>
                <Typography variant="body2" color="textSecondary">Due:
                    {formatDate(delinquencyItem.dueDate)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {payment}
                </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
                {icon}
                <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
                <Typography component="span">
                    {message}
                </Typography>
                <br />
                <Typography component="span">
                    {princBalance}
                </Typography>
            </TimelineContent>
        </TimelineItem>

    );
}

const DelinquencyComment2HTML = (nlsDelinquencyComment) => {
    let icon = <TimelineDot style={{backgroundColor: grey[400]}} ><NoteIcon /></TimelineDot>;
    let commentData = nlsDelinquencyComment.comment;
    let phoneRegex = /(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}/g;
    let chatRegex = /-------------------- Chat Log/g;
    let paymentRegex = /https:\/\//g;
    if(commentData.match(chatRegex)) {
        icon = <TimelineDot style={{backgroundColor: green[300]}} ><ChatBubbleIcon /></TimelineDot>;
    }
    else if(commentData.match(phoneRegex)) {
        icon = <TimelineDot style={{backgroundColor: blue[300]}} ><PhoneIcon /></TimelineDot>;
    }

    else if(commentData.match(paymentRegex)) {
        icon = <TimelineDot style={{backgroundColor: green[500]}} ><AttachMoneyIcon/></TimelineDot>;
    }


    return (
        <TimelineItem>
            <TimelineOppositeContent>
                <Typography variant="body2" color="textSecondary">
                    {formatDate(nlsDelinquencyComment.commentDate)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {formatTime(nlsDelinquencyComment.commentDate)}
                </Typography>
            </TimelineOppositeContent>

            <TimelineSeparator>
                {icon}
                <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
                <Typography style={{whiteSpace: 'pre-line'}} variant="body2" component="span">
                    {commentData}
                </Typography><br />
            </TimelineContent>
        </TimelineItem>
    );
}

const Loan = () => {
    const classes = styles();
    const { createErrorNotification } = useNotification();
    const { showError } = useError();
    const [comment, setComment] = React.useState("");
    const [category, setCategory] = React.useState(commentCategories[0].value);
    const [loanHistoryData, setLoanHistoryData] = useState(null);
    const [loanVisionFeeCalc, setLoanVisionFeeCalc] = useState(null);
    const [showVisionStats, setShowVisionStats] = useState(false);
    const [calcDisplay, setCalcDisplay] = useState(null);
    const [calculatorTitle, setCalculatorTitle] = useState('Calculator');
    const [commentData, setCommentData] = useState(null);
    const [currentCommentId, setCurrentCommentId] = useState(null);
    const [delinquencyCommentData, setLoanDelinquencyCommentData] = useState(null);
    const [loanDelinquencyData, setLoanDelinquencyData] = useState(null);
    const [loanMilestones, setLoanMilestones] = useState(null);
    const [showConfirmationBox, setShowConfirmationBox] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [showReturnsProcessedMarkerModal, setShowReturnsProcessedMarkerModal] = useState(false);
    const [markBouncedPayment, setMarkBouncedPayment] = useState(false);
    const [showFRPRmodal, setShowFRPRModal] = useState(false);
    const [alertData, setAlertData] = useState(null);
    const { loanNumber } = useParams();
    const { isDailyProcessingWrite, isAdmin, isDealerBrowser } = useRole();

    const history = createBrowserHistory();

    const toggleConfirmation = () => setShowConfirmationBox(!showConfirmationBox);
    const toggleAlert = () => setShowAlert(!showAlert);

    const handleCommentChange = (event) => {
        setComment(event.target.value);
    };

    const handleCategoryChange = (event) => {
        setCategory(event.target.value);
    };

    const toggleMarkBouncedClick = (event) => {
        setMarkBouncedPayment(!markBouncedPayment);
    };

    const handleFRPRModal = (event) => {
        setShowFRPRModal(!showFRPRmodal);
    }

    const toggleReturnProcessedModal = (event) => {
        setShowReturnsProcessedMarkerModal(!showReturnsProcessedMarkerModal);
    }

    const cycleAlert = () => {
        if(alertData.length - 1 === 0) toggleAlert();
        setAlertData(alertData.slice(1, alertData.length));
    }

    const toggleVisionStats = () => {
        setShowVisionStats(!showVisionStats);
    }

    const handleCommentSubmit = async (event) => {
        try {
            if (comment && comment.length > 0) {
                const response = await LoanService.newLoanComment(loanNumber, comment, category);

                if (isSuccessfulResponse(response)) {
                    // TODO: Improve me
                    setTimeout(() => window.location.reload(), 100);
                } else {
                    createErrorNotification(`Failed to save comment, please contact support`);
                }
            } else {
                createErrorNotification(`Can't create blank comment`);
            }
        } catch (error) {
            showError(error);
        }
    }

    const handleCommentDelete = async (event) => {
        try {
            if(currentCommentId) {
                const response = await LoanService.deleteLoanComment(currentCommentId, loanNumber);
                if (isSuccessfulResponse(response)) {
                    // TODO: Improve me
                    setTimeout(() => window.location.reload(), 100);
                } else {
                    createErrorNotification(`Failed to remove comment, please contact support`);
                }
            } else {
                createErrorNotification(`Failed to identify comment, please contact support`);
            }
        } catch (error) {
            showError(error);
        }
    }

    useEffect(() => {
        LoanService.getSingleLoanAPI(loanNumber)
            .then(data => { setLoanHistoryData(data.loan); })
            .catch(showError);

        if (loanNumber.length >= 9) {
            LoanService.getSingleLoanVisionCalculations(loanNumber).then(data => {
                if (data != null) {
                    setLoanVisionFeeCalc(data);
                }
            }).catch(() => {
                setLoanVisionFeeCalc(false);
            });
        }

        LoanService.getLoanCommentsForLoan(loanNumber)
            .then(data => setCommentData(data.comments))
            .catch(() => setCommentData(null));

        LoanService.getLoanPopupsForLoan(loanNumber).then(data => {
            setAlertData(data?.comments);
            if(data?.comments?.length > 0) setShowAlert(true);
        }).catch(() => setAlertData(null));

        LoanService.getLoanDelinquencyCommentsForLoan(loanNumber).then(data => setLoanDelinquencyCommentData(data?.comments));
        LoanService.getLoanMilestones(loanNumber).then(response => {
            setLoanMilestones(response?.data?.milestones)
        }).catch(() => setLoanMilestones([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanNumber]);

    useTheme();

    useEffect(() => {
        setCalculatorTitle(!showVisionStats ? 'Calculator' : 'Calculator (Vision)'  );

        const visionCalc = () => {
            return (loanVisionFeeCalc != null) ?
            (
                <Fragment>
                    <li>&nbsp;&nbsp;{centsToDollar(loanVisionFeeCalc.loanAmount)} <b>(Loan Amount)</b></li>
                    {loanVisionFeeCalc.activationCents && loanVisionFeeCalc.activationCents !== "0" ? <li>- {centsToDollar(loanVisionFeeCalc.activationCents)} <b>(Activation Fee)</b></li> : null}
                    {loanVisionFeeCalc.conciergeCents && loanVisionFeeCalc.conciergeCents !== "0" ? <li>- {centsToDollar(loanVisionFeeCalc.conciergeCents)} <b>(Concierge Fee)</b></li> : null}
                    {loanVisionFeeCalc.stageFundingCents ? <li>- {centsToDollar(loanVisionFeeCalc.stageFundingCents)} <b>(Staged Funding Cost)</b></li> : null}
                    {loanVisionFeeCalc.buydownCents ? <li>- {centsToDollar(loanVisionFeeCalc.buydownCents)} <b>(Buy Down Cost)</b></li> : null}
                    {Number(loanVisionFeeCalc.programCents) > 0 ? <li>- {centsToDollar(loanVisionFeeCalc.programCents)} <b>(Program Cost)</b></li> : null}
                    {loanVisionFeeCalc.promoCents ? <li>- {centsToDollar(loanVisionFeeCalc.promoCents)} <b>(Promo Cost)</b></li> : null}
                    {loanVisionFeeCalc.popCents ? <li>- {centsToDollar(loanVisionFeeCalc.popCents)} <b>(POP Promo)</b></li> : null}
                    {loanVisionFeeCalc.power360Cents ? <li>- {centsToDollar(loanVisionFeeCalc.power360Cents)} <b>(Power360 Promo)</b></li> : null}
                    <li>= {centsToDollar(loanVisionFeeCalc.dealerNetProceeds)} <b>(Dealer Net Proceeds)</b></li>
                </Fragment>
            ) : (null);
        }

        const defaultCalc = () => {
            return (loanHistoryData != null) ?
            (
                <Fragment>
                    <li>&nbsp;&nbsp;{centsToDollar(loanHistoryData.totalLoanAmountCents)} <b>(Loan Amount)</b></li>
                    {loanHistoryData.activationFeeCents ? <li>- {centsToDollar(loanHistoryData.activationFeeCents)} <b>(Activation Fee)</b></li> : null}
                    {loanHistoryData.conciergeFeeCents && loanHistoryData.conciergeFeeCents !== "0" ? <li>- {centsToDollar(loanHistoryData.conciergeFeeCents)} <b>(Concierge Fee)</b></li> : null}
                    {loanHistoryData.hasStagedFunding ? <li>- {centsToDollar(loanHistoryData.stagedFundingCostCents)} <b>(Staged Funding Cost)</b></li> : null}
                    {loanHistoryData.hasBuyDown ? <li>- {centsToDollar(loanHistoryData.buyDownCostCents)} <b>(Buy Down Cost)</b></li> : null}
                    {loanHistoryData.hasStipPay ? <li>- {centsToDollar(loanHistoryData.stipPayCostCents)} <b>(StipPay Cost)</b></li> : null}
                    {loanHistoryData.hasBalPay || loanHistoryData.hasOhno ? <li>- {centsToDollar(loanHistoryData.promoCostCents)} <b>(Promo Cost)</b></li> : null}
                    {Number(loanHistoryData.popPromoProgramFeeCents) > 0 ? <li>- {centsToDollar(loanHistoryData.popPromoProgramFeeCents)} <b>(Program Cost)</b></li> : null}
                    <li>= {centsToDollar(loanHistoryData.dealerNetProceedsCents)} <b>(Dealer Net Proceeds)</b></li>
                    {loanHistoryData.hasOhno || loanHistoryData.hasBalPay ? ((loanHistoryData.hasBalPay ? (<p><b>Balance Pay Promo</b></p>) : (<p><b>{loanHistoryData.ohnoPromoTerm} month OHNO Promo</b></p>))) : null}

                </Fragment>

            ) : (null);

        }

        setCalcDisplay(!showVisionStats ? defaultCalc : visionCalc);

    }, [showVisionStats, loanVisionFeeCalc, loanHistoryData]);

    function handleClick(event) {
        event.preventDefault();
        history.push("/lms/loans")
    }

    const handleShowConfirmationClick = (id) => {
        setCurrentCommentId(id);
        toggleConfirmation();
    }

    const handleDownloadCommentsFile = (id) => {
        LoanService.downloadCommentsFile(loanNumber).catch(showError)
    }

    const handleDownloadPCGCommentsFile = (loanNumber) => {
        LoanService.downloadPCGCommentsFile(loanNumber).catch(showError);
    }

    return (loanHistoryData === null || loanMilestones === null)
            ? (<h1>Loading...</h1>)
            : !loanHistoryData.hasOwnProperty('id')
                ? <h1>Failed to find loan by loanNumber: {loanNumber}</h1>
                :
                <div id="loans-container">
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link color="inherit" href="/lms/loans" onClick={handleClick}>
                            Loan Browser
                        </Link>
                        <Typography color="textPrimary">{loanNumber}</Typography>
                    </Breadcrumbs>
                    <div className={classes.pageHeader}>
                    <LoanTypeIcon className={classes.pageHeaderLoanType} loanType={loanHistoryData.loanType}/>
                        <h1>Loan: {loanNumber}</h1>
                        <Chip
                            className={classes.pageHeaderChip}
                            label={loanHistoryData.loanStatus.toUpperCase()}
                            style={{backgroundColor: getLoanStatusColor(loanHistoryData.loanStatus)}}
                        />
                    </div>

                    <Fragment>
                        <ReturnsProcessedMarkerModal onCancel={toggleReturnProcessedModal} refunds={loanHistoryData.refunds} showModal={showReturnsProcessedMarkerModal}/>
                        <ConfirmationModal title="Confirm" message="Are you sure you want to delete comment?" onConfirm={handleCommentDelete} onCancel={toggleConfirmation} showConfirmationBox={showConfirmationBox}/>
                        <BounceMarkerModal title="Confirm" message="Please select the payment or return that bounced" onCancel={toggleMarkBouncedClick} payments={loanHistoryData.payments} refunds={loanHistoryData.refunds} showBounceDialog={markBouncedPayment} loanNumber={loanNumber}/>
                        <FRPRModal
                            title="Prior Deleted FRs/Payments/Returns"
                            loanNumber={loanNumber}
                            open={showFRPRmodal}
                            onClose={handleFRPRModal}
                        />
                        <AlertModal title="A pop-up alert is on file:" message={alertData && alertData.length > 0 ? alertData[0].comment : ""} onAccept={cycleAlert} showAlertBox={showAlert}/>
                        {(loanMilestones.fundingMilestone || loanMilestones.paymentMilestone) && (
                            <div className={classes.milestones}>
                                <h2>Milestones:</h2>
                                {loanMilestones.fundingMilestone &&
                                    <Chip
                                        label={loanMilestones.fundingMilestone === 'pending' ? 'Pending CU Funding Completed Today' : 'CU Funding Completed'}
                                        style={{marginLeft: '15px', marginTop: '15px', backgroundColor: (loanMilestones.fundingMilestone === 'pending' ? blue[500] : green[500])}}
                                    />
                                }
                                {loanMilestones.paymentMilestone &&
                                    <Chip
                                        label={loanMilestones.paymentMilestone === 'pending' ? 'Pending Dealer Payments Completed Today' : 'Dealer Payments Completed'}
                                        style={{marginLeft: '15px', marginTop: '15px', backgroundColor: (loanMilestones.paymentMilestone === 'pending' ? blue[500] : green[500])}}
                                    />
                                }
                            </div>
                            )
                        }
                        {
                            loanHistoryData.hasChangeOrders && (
                                <div className={classes.milestones}>
                                    <h2>Alerts:</h2>

                                    {
                                        <Chip
                                            label={'Change Orders Present'}
                                            style={{marginLeft: '15px', marginTop: '15px', backgroundColor: yellow[500]}}
                                        />
                                    }
                                </div>
                            )
                        }
                        <Grid container direction="row">
                            <Grid item className={classes.summary}>
                                <h3 className={classes.summaryHeader}>Details</h3>
                                <ul className={classes.summaryUL}>
                                <li><b>Status Codes:</b> {formatCodes(loanHistoryData.loanStatusCode, isAdmin()).join(', ')}</li>
                                <Tooltip arrow placement="left" title={
                                    loanHistoryData.hasStagedFunding ?
                                    <Fragment>
                                        <div>Payment 1: {centsToDollar(loanHistoryData.firstStagePaymentCents)}</div>
                                        <div>Payment 2: {centsToDollar(loanHistoryData.secondStagePaymentCents)}</div>
                                        <div>Payment 3: {centsToDollar(loanHistoryData.thirdStagePaymentCents)}</div>
                                        <div>Payment 4: {centsToDollar(loanHistoryData.fourthStagePaymentCents)}</div>
                                        <div>Payment 5: {centsToDollar(loanHistoryData.finalStagePaymentCents)}</div>
                                    </Fragment> :
                                    `Payment Amount: ${centsToDollar(loanHistoryData.firstStagePaymentCents)}`}>
                                        <li><b>Dealer Payments:</b> {
                                            FundingStagesCell({
                                                "hasStagedFunding": loanHistoryData.hasStagedFunding,
                                                "paidStages": loanHistoryData.paidStages,
                                                "heldStages": loanHistoryData.heldStages,
                                                "numberOfPaymentStages": loanHistoryData.numberPaymentStages
                                            })}
                                        </li>
                                </Tooltip>
                                <li><b>Amount:</b> {centsToDollar(loanHistoryData.totalLoanAmountCents)}</li>
                                <li><b>Int Accrual:</b> {aprToPercent(loanHistoryData.intAccrualRate)}</li>
                                <li><b>APR:</b> {`${aprToPercent(loanHistoryData.apr)}${loanHistoryData.hasOhno ? ` (0% for ${loanHistoryData.ohnoPromoTerm} months)` : ''}`}</li>
                                <li><b>Term:</b> {loanHistoryData.termMonths}</li>
                                <li><b>Project Type:</b> {loanHistoryData.projectType}</li>
                                <li><b>Structure Tier:</b> {loanHistoryData.structureTier}</li>
                                {
                                    loanHistoryData.visionId &&
                                    <li><b>Vision Link:</b> <a href={loanHistoryData.visionLoanUrl} target="_blank" rel="noopener noreferrer">Go to Vision Page</a></li>
                                }
                                </ul>
                            </Grid>
                            <Grid item className={classes.summary}>
                                <h3 className={classes.summaryHeader}>People</h3>
                                <ul className={classes.summaryUL}>
                                <li><b>Borrower:</b> {loanHistoryData.applicant?.fullName}</li>
                                <li><b>Borrower Email:</b> {loanHistoryData.applicant?.emailAddress}</li>
                                {loanHistoryData.coapplicant ? <li><b>CoBorrower:</b> {loanHistoryData.coapplicant?.fullName}</li> : null}
                                {loanHistoryData.coapplicant ? <li><b>CoBorrower Email:</b> {loanHistoryData.coapplicant?.emailAddress}</li> : null}
                                <li><b>Dealer Number:</b> {isAdmin() || isDealerBrowser() ? <a href={`/dealers/${loanHistoryData.dealer?.dealerNumber}`}>{loanHistoryData.dealer?.dealerNumber}</a> : loanHistoryData.dealer?.dealerNumber}</li>
                                <li><b>Dealer:</b> {loanHistoryData.dealer?.name}</li>
                                <Tooltip title="Merchant app count at time of P4 loan creation: TOTAL / FUNDED / IN PROGRESS"><li><b>Dealer Apps:</b> {loanHistoryData.merchantAppCount}</li></Tooltip>
                                <li><b>Organization:</b> {loanHistoryData.dealer?.organization?.name}</li>
                                <li><b>Sponsor-Affiliate:</b> {loanHistoryData.sponsorAffiliate}</li>
                                <li><b>Concierge Dealer:</b> {(loanHistoryData.dealer?.isConcierge ? "Yes" : "No")}</li>
                                <li><b>Lender:</b> {loanHistoryData.lender?.shortName}</li>
                                </ul>
                                {loanHistoryData.dealer?.isManualFlagged ? (<p><b>Warning: Dealer is manually flagged because '{loanHistoryData.dealer?.manualFlaggedReason}'</b></p>) : null}
                            </Grid>
                            <Grid item className={classes.summary}>
                                <h3 className={classes.summaryHeader}>{calculatorTitle}</h3>
                                <ul className={classes.calculatorUL}>
                                    {calcDisplay}
                                </ul>
                                <h3 className={classes.summaryHeader}>Payments</h3>
                                <ul className={classes.summaryUL}>
                                    <li><b>Monthly Payment:</b> {loanHistoryData.monthlyPaymentCents ? centsToDollar(loanHistoryData.monthlyPaymentCents) : 'N/A'}</li>
                                    <li><b>Current Amount Due:</b> {loanHistoryData.currentAmountDueCents ? centsToDollar(loanHistoryData.currentAmountDueCents) : 'N/A'}</li>
                                    <li><b>Interest Start Date:</b> {loanHistoryData.interestStartDate ? formatDate(loanHistoryData.interestStartDate) : 'N/A'}</li>
                                    <li><b>Date Last Paid:</b> {loanHistoryData.dateLastPaid ? formatDate(loanHistoryData.dateLastPaid) : 'N/A'}</li>
                                    <li><b>Next Due Date:</b> {loanHistoryData.nextDueDate ? formatDate(loanHistoryData.nextDueDate) : 'N/A'}</li>
                                    <li><b>Total Past Due:</b> {loanHistoryData.totalPastDueCents ? centsToDollar(loanHistoryData.totalPastDueCents) : 'N/A'}</li>
                                    <li><b>Total Balance Due:</b> {loanHistoryData.totalBalanceDueCents ? centsToDollar(loanHistoryData.totalBalanceDueCents) : 'N/A'}</li>
                                </ul>

                                { (loanVisionFeeCalc === false) ?
                                    (null) : (
                                        <Tooltip title="Toggle Vision Calculator">
                                            <IconButton
                                                className={classes.calculatorBtn}
                                                onClick={() => toggleVisionStats()}>
                                                <DialpadIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    )
                                }
                            </Grid>
                        </Grid>

                        <Grid container direction="row">
                            <Grid item className={classes.secondRow}>
                                <h3 className={classes.summaryHeader}>Funding History</h3>
                                <div className={classes.historyTimelineWrapper}>
                                    <Timeline>
                                        {gatherHistory(loanHistoryData)}
                                    </Timeline>
                                </div>
                                {isAdmin() && isDailyProcessingWrite()
                                    ? <div className={classes.summaryDownload}>
                                        <Tooltip title="Mark Return as Processed">
                                            <IconButton onClick={() => toggleReturnProcessedModal()}>
                                                <AssignmentTurnedIn/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Deleted FR, Payments, & Icons">
                                            <IconButton onClick={() => handleFRPRModal()}>
                                                <Dvr/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Mark Bounced Payments and Returns">
                                            <IconButton onClick={() => toggleMarkBouncedClick()}>
                                                <MoneyOffIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                    : null
                                }
                            </Grid>

                            <Grid item className={classes.secondRow}>
                                <h3 className={classes.summaryHeader}>Notes</h3>
                                <form className={classes.root} noValidate autoComplete="off">
                                    <TextField
                                        id="outlined-select-currency-native"
                                        select
                                        label="Category"
                                        value={category}
                                        onChange={handleCategoryChange}
                                        className={classes.commentType}
                                        SelectProps={{
                                            native: true,
                                        }}
                                        variant="outlined"
                                        >
                                        {commentCategories.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.value}
                                            </option>
                                        ))}
                                    </TextField>
                                    <TextField id="outlined-basic" label="Add a comment..." value={comment} onChange={handleCommentChange} variant="outlined" multiline className={classes.comment}/>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.commentButton}
                                        endIcon={<SendIcon/>}
                                        onClick={handleCommentSubmit}
                                    >
                                        Submit
                                    </Button>
                                </form>
                                <div className={classes.notesWrapper}>
                                    <List className={classes.root}>
                                        {
                                            (commentData === null) ?
                                            (<h1> </h1>) :
                                            commentData.map(currentComment => {
                                                return (
                                                    <Fragment>
                                                        <Divider variant="inset" component="li" />
                                                        <ListItem alignItems="flex-start">
                                                            <ListItemAvatar>
                                                                <Avatar alt={currentComment.commenter} src="/does/not/exist/1.jpg" />
                                                            </ListItemAvatar>
                                                            <ListItemText
                                                                primary={
                                                                    <Fragment>
                                                                        <Typography
                                                                            component="span"
                                                                            className={`${classes.inline}`}
                                                                        >
                                                                            {currentComment.commenter} -
                                                                        </Typography>
                                                                        <Typography
                                                                            component="span"
                                                                            variant="body2"
                                                                            className={`${classes.inline} ${classes.commentDivider}`}
                                                                        >
                                                                            {currentComment.category}
                                                                        </Typography>
                                                                        <Typography
                                                                            component="span"
                                                                            className={`${classes.inline} ${classes.commentDate}`}
                                                                        >
                                                                        {`(${timeAgo(currentComment.date)})`}
                                                                        </Typography>
                                                                    </Fragment>
                                                                }
                                                                secondary={
                                                                    <Typography
                                                                        component="span"
                                                                        variant="body1"
                                                                        className={classes.commentBody}
                                                                        color="textPrimary"
                                                                    >
                                                                        {currentComment.comment}
                                                                    </Typography>

                                                                }
                                                            />
                                                            {isAdmin() ? <IconButton
                                                                onClick={() => handleShowConfirmationClick(currentComment.id)}>
                                                                <CloseIcon/>
                                                            </IconButton>:''}
                                                        </ListItem>
                                                    </Fragment>
                                                );
                                            })
                                        }
                                    </List>
                                </div>
                                <div className={classes.summaryDownload}>
                                    <Tooltip title="Download Notes">
                                        <IconButton onClick={() => handleDownloadCommentsFile(loanNumber)}>
                                            <CloudDownloadIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </Grid>
                            {
                            (loanHistoryData.delinquencyHistory && loanHistoryData.delinquencyHistory.length > 0) ?
                                <Grid item className={classes.secondRow}>
                                    <h3 className={classes.summaryHeader}>Delinquency Record</h3>
                                    <div className={classes.historyTimelineWrapper}>
                                        <Timeline>
                                            {loanHistoryData.delinquencyHistory ? loanHistoryData.delinquencyHistory.map(Delinquency2HTML) : null}
                                        </Timeline>
                                    </div>
                                </Grid>
                                : null
                            }
                            {
                                (delinquencyCommentData && delinquencyCommentData.length > 0) &&
                                <Grid item className={classes.secondRow}>
                                    <h3 className={classes.summaryHeader}>PCG Delinquency Notes</h3>
                                    <div className={classes.historyTimelineWrapper}>
                                        <Timeline>
                                            {delinquencyCommentData ? delinquencyCommentData.sort((a,b) => {
                                                return Date.parse(b.commentDate) - Date.parse(a.commentDate);
                                            }).map(DelinquencyComment2HTML) : null}
                                        </Timeline>
                                    </div>
                                    <div className={classes.summaryDownload}>
                                        <Tooltip title="Download PCG Notes">
                                            <IconButton onClick={() => handleDownloadPCGCommentsFile(loanNumber)}>
                                                <CloudDownloadIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </Grid>
                            }
                            <Grid item className={classes.secondRow}>
                                <h3 className={classes.summaryHeader}>Loan Notifications</h3>
                                 <LoanNotificationsModal loanNumber={loanNumber} />
                            </Grid>
                            {
                                isDailyProcessingWrite() || isAdmin()
                                ? <Grid item className={classes.secondRow}>
                                    <h3 className={classes.summaryHeader}>Loan Attributes History</h3>
                                    <HistoryDiff logData={loanHistoryData.logData} />
                                </Grid>
                                : null
                            }
                        </Grid>
                    </Fragment>
                </div>
}

export default Loan;
